import { currentResultClass } from '@tiptap/extension-find-and-replace';
import { Editor, useEditorState } from '@tiptap/react';
import {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { Button, ButtonType } from '../../Button';
import { IconLabel } from '../../IconLabel';
import { InputField } from '../../InputField';

import classNames from 'classnames';
import classes from './FindReplaceBar.module.scss';

declare module '@tiptap/core' {
  interface EditorEvents {
    openFindReplace: [];
  }
}

interface FindReplaceBarProps {
  editor: Editor;
}

export const FindReplaceBar: FunctionComponent<FindReplaceBarProps> = ({ editor }) => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'richTextEditor' });

  const { total, currentIndex, appliedSearchTerm } = useEditorState({
    editor,
    selector: ({ editor }) => ({
      total: editor.storage.findAndReplace.results.length,
      currentIndex: editor.storage.findAndReplace.currentIndex,
      appliedSearchTerm: editor.storage.findAndReplace.searchTerm,
    }),
  });

  const [isOpen, setIsOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');

  const [replaceTerm, setReplaceTerm] = useState('');

  const searchInputRef = useRef<HTMLInputElement>(null);

  const hasNoResults = total === 0;

  const counterLabel = useMemo(() => {
    if (!appliedSearchTerm) {
      return null;
    }

    if (hasNoResults) {
      return ctxT('find-no-results');
    }

    return `${(currentIndex ?? 0) + 1} ${t('common.of')} ${total}`;
  }, [appliedSearchTerm, ctxT, currentIndex, hasNoResults, t, total]);

  const openHandler = useCallback(() => {
    const { from, to, $from, $to } = editor.state.selection;

    const selectedText = editor.state.doc.textBetween(from, to, ' ').trim();

    if (selectedText && editor.view.hasFocus() && $from.sameParent($to)) {
      setSearchTerm(selectedText);
      editor.commands.setSearchTerm(selectedText);
    }

    setIsOpen(true);

    setTimeout(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    }, 0);
  }, [editor]);

  const closeHandler = useCallback(() => {
    editor.chain().clearSearch().setReplaceTerm('').focus().run();
    setSearchTerm('');
    setReplaceTerm('');
    setIsOpen(false);
  }, [editor]);

  const changeSearchTermHandler = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(value);
      editor.commands.setSearchTerm(value);
    },
    [editor]
  );

  const changeReplaceTermHandler = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setReplaceTerm(value);
      editor.commands.setReplaceTerm(value);
    },
    [editor]
  );

  const scrollToCurrentMatch = useCallback(
    () =>
      editor.view.dom
        .querySelector(`.${currentResultClass}`)
        ?.scrollIntoView({ block: 'nearest', inline: 'nearest' }),
    [editor]
  );

  const goToPreviousHandler = useCallback(() => {
    editor.commands.goToPreviousResult();
    scrollToCurrentMatch();
  }, [editor, scrollToCurrentMatch]);

  const goToNextHandler = useCallback(() => {
    editor.commands.goToNextResult();
    scrollToCurrentMatch();
  }, [editor, scrollToCurrentMatch]);

  const replaceHandler = useCallback(() => {
    editor.commands.replace();
    scrollToCurrentMatch();

    if (!editor.storage.findAndReplace.results.length) {
      searchInputRef.current?.focus();
    }
  }, [editor, scrollToCurrentMatch]);

  const replaceAllHandler = useCallback(() => {
    editor.commands.replaceAll();
    searchInputRef.current?.focus();
  }, [editor]);

  const searchKeyDownHandler = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter' || event.nativeEvent.isComposing) {
        return;
      }

      event.preventDefault();

      if (event.shiftKey) {
        goToPreviousHandler();
        return;
      }

      goToNextHandler();
    },
    [goToNextHandler, goToPreviousHandler]
  );

  const replaceKeyDownHandler = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter' || event.nativeEvent.isComposing || hasNoResults) {
        return;
      }

      event.preventDefault();
      replaceHandler();
    },
    [hasNoResults, replaceHandler]
  );

  const barKeyDownHandler = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.stopPropagation();
      closeHandler();
    },
    [closeHandler]
  );

  useEffect(() => {
    const windowKeyDownHandler = (event: KeyboardEvent) => {
      const isFindShortcut =
        (event.metaKey || event.ctrlKey) &&
        !event.shiftKey &&
        !event.altKey &&
        event.key.toLowerCase() === 'f';

      if (!isFindShortcut || !editor.view.dom.offsetParent) {
        return;
      }

      event.preventDefault();
      openHandler();
    };

    window.addEventListener('keydown', windowKeyDownHandler);

    return () => window.removeEventListener('keydown', windowKeyDownHandler);
  }, [editor, openHandler]);

  useEffect(() => {
    editor.on('openFindReplace', openHandler);

    return () => {
      editor.off('openFindReplace', openHandler);
    };
  }, [editor, openHandler]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const editorElement = editor.view.dom;

    const editorKeyDownHandler = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.stopPropagation();
      closeHandler();
    };

    editorElement.addEventListener('keydown', editorKeyDownHandler);

    return () => editorElement.removeEventListener('keydown', editorKeyDownHandler);
  }, [closeHandler, editor, isOpen]);

  useEffect(() => {
    scrollToCurrentMatch();
  }, [appliedSearchTerm, scrollToCurrentMatch]);

  if (!isOpen) {
    return null;
  }

  const closeIcon = (
    <IconLabel
      iconId={'close'}
      iconSize={20}
      ariaLabel={ctxT('find-close')}
      className={classes['find-replace-bar__close']}
      onClick={closeHandler}
    />
  );

  return (
    <div
      className={classNames(classes['find-replace-bar'], {
        [classes['find-replace-bar--mobile']]: isMobileOnly,
      })}
      tabIndex={-1}
      onKeyDown={barKeyDownHandler}
    >
      <div className={classes['find-replace-bar__group']}>
        <InputField
          ref={searchInputRef}
          value={searchTerm}
          placeholder={t('common.search')}
          className={classes['find-replace-bar__field']}
          onChange={changeSearchTermHandler}
          onKeyDown={searchKeyDownHandler}
        />

        <div className={classes['find-replace-bar__controls']}>
          {counterLabel && (
            <span className={classes['find-replace-bar__counter']}>{counterLabel}</span>
          )}

          <IconLabel
            iconId={'up-arrow'}
            iconSize={20}
            ariaLabel={ctxT('find-previous')}
            onClick={goToPreviousHandler}
            disabled={hasNoResults}
          />

          <IconLabel
            iconId={'down-arrow'}
            iconSize={20}
            ariaLabel={ctxT('find-next')}
            onClick={goToNextHandler}
            disabled={hasNoResults}
          />

          {isMobileOnly && closeIcon}
        </div>
      </div>

      <div className={classes['find-replace-bar__group']}>
        <InputField
          value={replaceTerm}
          placeholder={ctxT('find-replace-placeholder')}
          className={classes['find-replace-bar__field']}
          onChange={changeReplaceTermHandler}
          onKeyDown={replaceKeyDownHandler}
        />

        <div className={classes['find-replace-bar__controls']}>
          <Button
            type={ButtonType.secondary}
            label={ctxT('find-replace')}
            onClick={replaceHandler}
            disabled={hasNoResults}
          />

          <Button
            type={ButtonType.secondary}
            label={ctxT('find-replace-all')}
            onClick={replaceAllHandler}
            disabled={hasNoResults}
          />
        </div>
      </div>

      {!isMobileOnly && closeIcon}
    </div>
  );
};
